// Real-time layer for the teacher/staff live board — the school-wide
// counterpart to useStudentPassEvents.js. TEACHER (and ADMIN) sockets are
// auto-joined to `school:{schoolId}` by the server on connect
// (hallpass-backend's io.on("connection", ...) handler), so this hook
// receives events for every student's pass in the school, not just one
// student's `user:{userId}` room. There is no client-side room-join message
// to send.
//
// Events: passes-api emits PassResponse payloads on 'pass:requested',
// 'pass:approved', 'pass:waiting', 'pass:denied', 'pass:returned',
// 'pass:cancelled', 'pass:expired' (hallpass-backend/packages/types/src/
// socket.ts's PassEvent map). Unlike useStudentPassEvents.js, 'pass:requested'
// IS subscribed here — it's staff-only and this is a staff-facing board.
//
// The connect/reconnect/resync/terminal-status-guard mechanics live in the
// shared usePassSocket.js base hook — see that file for the full contract
// (no event replay on reconnect, resync-on-connect, terminal-state guard).

import { useCallback } from 'react';
import { usePassSocket } from './usePassSocket';
import type { Pass, UsePassSocketResult } from './usePassSocket';

const PASS_EVENT_NAMES = [
  'pass:requested',
  'pass:approved',
  'pass:waiting',
  'pass:denied',
  'pass:returned',
  'pass:cancelled',
  'pass:expired',
];

/**
 * Real-time + REST-backed view of the whole school's passes, for the
 * teacher/staff live board.
 */
export interface UseSchoolPassesResult {
  passes: Pass[];
  connectionStatus: UsePassSocketResult['connectionStatus'];
  lastEvent: UsePassSocketResult['lastEvent'];
  refresh: () => Promise<void>;
  getPassesForClassSection: (classSectionId: number) => Pass[];
}

export function useSchoolPasses(): UseSchoolPassesResult {
  const { passes, connectionStatus, lastEvent, resync } = usePassSocket(
    PASS_EVENT_NAMES,
    'useSchoolPasses'
  );

  // Also includes classSectionId === null passes alongside the requested
  // section's passes. These are students who haven't been scheduled into a
  // section yet (POST /passes fails open rather than blocking pass
  // creation - see passes-api's create-pass route), and TeacherDashboard.js's
  // default (non-"All Class Sections") view needs to surface them rather
  // than silently hiding them. This helper currently has exactly one
  // caller (TeacherDashboard.js's default queue); a future caller that
  // needs a strict single-section filter (e.g. a specific section's
  // roster/analytics) should not reuse it as-is.
  const getPassesForClassSection = useCallback(
    (classSectionId: number): Pass[] =>
      passes.filter(
        (pass: Pass) =>
          pass.classSectionId === classSectionId || pass.classSectionId === null
      ),
    [passes]
  );

  const refresh = useCallback((): Promise<void> => resync(), [resync]);

  return {
    passes,
    connectionStatus,
    lastEvent,
    refresh,
    getPassesForClassSection,
  };
}

export default useSchoolPasses;
